/* eslint camelcase: "off" */

import EventEmitter from 'events'

import ensureZepto from 'amkit3-modules/utils/ensure-zepto'
import { getLogger } from 'amkit3-modules/utils/log4js'

import {
  requestFullscreen,
  exitFullscreen,
  isFullscreen,
  fullscreenChange,
} from 'amkit3-modules/utils/fullscreen'

import playbackFactory from 'amkit3-modules/v/playbacks/factory'
import HuyaUI from 'amkit3-modules/v/ui/huya'
import HuyaDanmu from 'amkit3-modules/v/danmu/huya'
import playerProfile from 'amkit3-modules/v/player/profile'

import UIEvent from 'amkit3-modules/v/ui/ui-event'
import DanmuEvent from 'amkit3-modules/v/danmu/danmu-event'
import HuyaUIEvent from 'amkit3-modules/v/ui/huya/huya-ui-event'
import PlaybackEvent from 'amkit3-modules/v/playbacks/playback-event'

import FeedbackSdk from 'amkit3-modules/utils/feedbackSdk'
import packjson from '../../../../package.json'
import Toast from 'amkit3-modules/components/huya/Toast'
import {
  video_danmu_switch,
  videoLiveDanmuAllowList,
} from 'amkit3-modules/utils/services/huya/whitelist'
import Statistic from './statistic'
import HuyaPlayerInfo from './info'
import HuyaAd from './ad'
import * as HuyaAdEvent from './ad-event'

import ContinuousPlugin from './plugins/continuous'
import EndedRecommendPlugin from './plugins/ended-recommend'
import { listenNotice } from 'amkit3-modules/utils/taf/'

export const TIME = {
  /**  sdk初始化 */
  INIT: 0,
  /** 播放器触发播放到播放 */
  PLAYEND: 0,
  /** sdk初始化到播放器触发播放 */
  PLAYSTART: 0,
  /**  获取播放器信息时间 */
  INFO_START: 0,
  INFO_END: 0,
  BEFORE_PLAY: 0,
}

const logger = getLogger('v/player/huya/core')

class HuyaPlayerCore extends EventEmitter {
  // 构造函数
  constructor(container, options, ready) {
    TIME.INIT = new Date().getTime()

    // 调用父类构造函数
    super()

    // 确保zepto库可用
    ensureZepto((err, $) => {
      // 如果zepto库不可用，则输出警告信息
      if (err) {
        logger.warn('no zepto supported, err[', err, ']')
        // 调用ready函数，并传入错误信息
        ready(err)
      } else {
        // 如果zepto库可用，则调用init函数，并传入zepto库、容器、选项和ready函数
        this.init($, container, options, ready)
      }
    })
  }
  // 初始化huya播放器
  init($, container, options, ready) {
    // 打印日志
    logger.debug('huya player init')
    logger.debug('container[', container, ']')
    logger.debug('options[', options, ']')
    // 保存传入的参数
    this.$ = $
    this.$container = this.$(container)
    // 引入样式文件
    this.style = require('./style.scssm')
    // 引入选择器
    this.selector = require('./selector')
    console.log('options', options)
    // 保存传入的参数
    this.options = options
    // 将模板添加到容器中
    this.$container.append(
      this.$(
        require('./template.html')({
          style: this.style,
        }),
      ),
    )
    // 保存容器中的各个元素
    this.$playerContainer = this.$container.find(this.selector.containerMain)
    this.$coverContainer = this.$container.find(this.selector.coverContainer)
    this.$danmuContainer = this.$container.find(this.selector.danmuContainer)
    this.$adContainer = this.$container.find(this.selector.adContainer)
    this.$playbackContainer = this.$container.find(this.selector.playbackContainer)
    this.$uiContainer = this.$container.find(this.selector.uiContainer)
    this.timing = {}
    this.lastRecordTime = 0 // 记录上次保存播放进度的时间

    // 如果传入的参数中包含no_ui，则隐藏容器中的各个元素
    if (this.options.no_ui) {
      this.$uiContainer.hide()
      this.$coverContainer.hide()
      this.$danmuContainer.hide()
      this.$playbackContainer.css({ width: '100%', height: '100%' })
    }

    // 合并传入的参数和默认参数
    this.options = this.$.extend(
      {
        no_ad: false,
        autoplay: false,
        no_danmu: false,
        from: 'vhuyaweb',
        isShowLiveDanmu: false,
        enableH265: false,
      },
      {
        autoplay: Number(options.auto_play) === 1,
      },
      options,
    )
    this.ui = new HuyaUI(this.$uiContainer, this.options, this, err => {
      if (err) {
        logger.warn('init ui failed, err[', err, ']')
        this.$playerContainer.remove()
        return ready(err)
      }
      // this.ui.enable()
      this.ui.setBuffering(true)
      // this.ui.disable()
      TIME.INFO_START = new Date().getTime()
      this.initInfo(ready)
    })
  }
  initInfo(ready) {
    if (this.options.vid) {
      logger.debug('get info for vid[', this.options.vid, ']')
      this.info = new HuyaPlayerInfo(this.options)
      console.log('this.info', this.info)
      this.info.init(err => {
        if (err) {
          logger.debug('get info for vid[', this.options.vid, '] failed, err[', err, ']')
          this.ui.setError(err)
        } else {
          TIME.INFO_END = new Date().getTime()

          let definition = this.options.definition || playerProfile.getDefinition()
          logger.debug('definition is set to [', definition, ']')
          this.info.setDefinition(definition)
          this.checkPlayback(ready)
          // this.$coverContainer.css({
          //   'background-image': 'url(' + this.info.getCover() + ')'
          // })
        }
      })
    } else {
      logger.warn('no vid in options')
      this.ui.setError(new Error('vid is required in options'))
    }
  }
  startPlay(callback, extraOptions = { seekTime: 0 }) {
    const definitionConfig = this.info.getDefinitionConfig()
    const lastPlayTime = this.getLastPlayTime()
    // 初始化播放器
    playbackFactory.get(
      this.$playbackContainer[0],
      this.$.extend(
        {},
        this.options,
        {
          ...definitionConfig,
          controls: this.options.no_ui,
          src: definitionConfig.url,
          playerConfig: this.info.playerConfig,
          source: this.options.source,
        },
        extraOptions,
        { seekTime: lastPlayTime || 0 },
      ),
      callback,
    )
  }
  checkPlayback(ready) {
    this.startPlay((err, PlaybackConstructor, playback) => {
      if (err) {
        logger.warn('checkPlayback failed, err[', err, ']')
        ready(err)
      } else {
        logger.debug('checkPlayback success, constructor[', PlaybackConstructor.name, ']')
        this.playback = playback
        TIME.BEFORE_PLAY = new Date().getTime()
        this.initPlayer(ready)
      }
    })
  }
  initPlayer(ready) {
    this.initPlayback()

    // 播放应该提前
    logger.debug('initPlayer')
    if (this.options.autoplay) {
      logger.debug('autoplay')
      this.$coverContainer.remove()
      this.playbackPlay()
      if (isSafari) {
        this.ui.setPlay()
      }
    } else {
      // ORIGIN EVENTS
      logger.debug('not autoplay, wait for play button click')
      this.ui.enable()
      this.ui.setBuffering(false)
      this.ui.disable()
      this.ui.once(UIEvent.PLAY_BUTTON_CLICK_O, () => {
        this.playbackPlay()
        this.emit('CLICK_PLAY')
      })
    }

    this.initUI()
    this.initStatistic()
    let volume = playerProfile.getVolume()
    logger.debug('volume in profile is [', volume, ']')
    this.playback.setVolume(volume)
    this.ui.setVolume(volume)
    this.initPlugins()
    ready()
    const ua = navigator.userAgent.toLowerCase()
    const isSafari = ua.indexOf('safari') !== -1 && ua.indexOf('chrome') === -1
    this.initAd()
    this.initDanmu()
    this.initFeedBack()
    this.initListenFeedback()
  }
  initPlugins() {
    this.continuousPlugin = new ContinuousPlugin(
      this.$,
      this.$container,
      this.options,
      this.playback,
      this.ui,
    )
    this.endedRecommendPlugin = new EndedRecommendPlugin(
      this.$,
      this.$container,
      this.options,
      this.playback,
      this.ui,
    )
  }
  initUI() {
    logger.debug('init ui', this.ui)
    this.ui.enable()
    let duration = this.info.getDefinitionConfig().duration
    logger.debug('duration[', duration, ']')
    let definition = this.info.getDefinitionConfig().definition
    logger.debug('definition[', definition, ']')
    this.ui.setDuration(duration)
    this.ui.setTime(0)
    this.ui.setDefinitions(this.info.getDefinitions())
    this.ui.setDefinition(definition)
    this.ui.disable()
    this.delegateOnUI(UIEvent.PLAY_BUTTON_CLICK, () => {
      logger.debug('ui PLAY_BUTTON_CLICK emitted')
      this.$coverContainer.remove()
      this.playback.play()
    })
    this.delegateOnUI(UIEvent.PAUSE_BUTTON_CLICK, () => {
      logger.debug('ui PAUSE_BUTTON_CLICK emitted')
      this.playback.pause()
    })
    this.delegateOnUI(UIEvent.TIME_CHANGE, () => {
      let time = this.ui.getTime()
      logger.debug('ui TIME_CHANGE emitted, time[', time, ']')
      this.playback.seek(this.ui.getTime())
      this.lastRecordTime = time // 重置记录时间
      this.recordPlayTime()
    })
    this.delegateOnUI(UIEvent.VOLUME_CHANGE, () => {
      let volume = this.ui.getVolume()
      logger.debug('ui VOLUME_CHANGE emitted, volume[', volume, ']')
      playerProfile.setVolume(volume)
      this.playback.setVolume(volume)
    })
    this.delegateOnUI(HuyaUIEvent.DEFINITION_CHANGE, () => {
      this.ui.setBuffering(true)
      let definition = this.ui.getDefinition()
      logger.debug('ui DEFINITION_CHANGE emitted, definition[', definition, ']')
      this.definitionChangeTime = this.playback.getCurrentTime()
      logger.debug('before definition change, time[', this.definitionChangeTime, ']')
      logger.debug('destroy current playback')
      this.playback.removeAllListeners()
      this.playback.destroy()
      this.$playbackContainer.empty()
      logger.debug('disable ui')
      this.ui.disable()
      playerProfile.setDefinition(definition)
      this.info.setDefinition(definition)
      this.playback.setCodeRate(definition)
      this.startPlay(
        (err, PlaybackConstructor, playback) => {
          if (err) {
            logger.warn(
              'initialize new playback for src[',
              definition.url,
              '] failed, err[',
              err,
              ']',
            )
            this.ui.setError(err)
          } else {
            logger.debug(
              'initialize new playback for src[',
              definition.url,
              '] success, constructor[',
              PlaybackConstructor.name,
              ']',
            )
            this.playback = playback

            this.playback.once(PlaybackEvent.LOAD_METADATA, () => {
              logger.debug('new playback for src[', definition.url, '] ready')
              let volume = this.ui.getVolume()
              logger.debug('reset volume[', volume, '] in new playback')
              this.playback.setVolume(volume)
              this.playback.setPlaybackRate(this.ui.getPlaybackRate())
              this.lastRecordTime = this.definitionChangeTime || 0 // 重置记录时间
              this.ui.setPause()
            })
            this.initPlayback()
            this.initPlugins()
            logger.debug('enable ui')
            this.ui.enable()
            console.log('xxxxxxx')
            this.playback.play()
          }
        },
        { seekTime: this.definitionChangeTime },
      )
    })
    this.delegateOnUI(HuyaUIEvent.PLAYBACK_RATE_CHANGE, () => {
      this.playback.setPlaybackRate(this.ui.getPlaybackRate())
    })
    this.delegateOnUI(UIEvent.FULLSCREEN_BUTTON_CLICK, () => {
      logger.debug('ui FULLSCREEN_BUTTON_CLICK emitted')
      if (isFullscreen()) {
        logger.debug('exit fullscreen')
        exitFullscreen()
      } else {
        logger.debug('enter fullscreen')
        requestFullscreen(this.$playerContainer[0])
      }
    })
    fullscreenChange(() => {
      logger.debug('fullscreen status change')
      this.ui.containerChange()
      if (isFullscreen()) {
        this.$playerContainer.addClass(this.style.fullscreen)
      } else {
        this.$playerContainer.removeClass(this.style.fullscreen)
      }
    })
    this.delegateOnUI(UIEvent.REPLAY_BUTTON_CLICK, () => {
      logger.debug('ui REPLAY_BUTTON_CLICK emitted')
      this.playback.play()
      this.endedRecommendPlugin.hide()
    })
    this.delegateOnUI(DanmuEvent.ENABLE, () => {})
    this.delegateOnUI(DanmuEvent.DISABLE, () => {})
    this.delegateOnUI(DanmuEvent.SIGNIN, () => {})
    this.delegateOnUI(DanmuEvent.SIGNUP, () => {})
    this.delegateOnUI(DanmuEvent.SUBMIT, () => {})

    this.delegateOnUI(DanmuEvent.POSITION, position => {})
    this.delegateOnUI(DanmuEvent.SIZE, size => {})
    this.delegateOnUI(DanmuEvent.TYPE, type => {})
    this.delegateOnUI(DanmuEvent.TYPE_CHANGE, props => {})
    this.delegateOnUI(DanmuEvent.OPACITY, opacity => {})
    this.delegateOnUI(DanmuEvent.OPACITY_END, opacity => {})

    this.delegateOnUI(UIEvent.FEEDBACK_SUBMIT, data => {
      console.log('on feedback')
      this.sendFeedback(data)
    })
    this.playback.hyplayer.on('AUTO_LOG', () => {
      console.log('AUTO_LOG', this.emit, UIEvent.FEEDBACK_SUBMIT)
      this.sendFeedback(
        {
          errorText: 'SDK自动反馈',
          content: 'SDK自动反馈',
          detail: 'SDK自动反馈',
          qq: '',
        },
        true,
      )
    })
  }
  // 记录播放时间 - 使用数组存储，最多保存20个vid
  recordPlayTime() {
    // 只有视频时长超过60秒才记录
    const duration = this.playback.getDuration()
    if (duration <= 60) {
      return
    }

    const currentTime = this.playback.getCurrentTime()

    // 如果接近结束时间（最后5秒），不记录
    if (duration - currentTime <= 5) {
      return
    }

    if (currentTime >= 0) {
      const vid = this.options.vid

      // 获取现有记录
      let list = []
      try {
        const saved = localStorage.getItem('playbackTimeList')
        if (saved) list = JSON.parse(saved)
      } catch (e) {
        list = []
      }

      // 移除旧记录（如果存在）
      list = list.filter(item => item.vid !== vid)

      // 添加新记录到开头
      list.unshift({ vid, time: currentTime })

      // 只保留最新20条
      if (list.length > 20) {
        list = list.slice(0, 20)
      }

      // 保存
      localStorage.setItem('playbackTimeList', JSON.stringify(list))
    }
  }

  // 读取播放进度
  getLastPlayTime() {
    try {
      const vid = this.options.vid
      const saved = localStorage.getItem('playbackTimeList')
      if (!saved) return 0

      const list = JSON.parse(saved)
      const record = list.find(item => +item.vid === +vid)
      return record ? record.time : 0
    } catch (e) {
      console.log('record', e)
      return 0
    }
  }

  // 清空播放时间记录
  clearPlayTime() {
    try {
      const vid = this.options.vid
      const saved = localStorage.getItem('playbackTimeList')
      if (!saved) return

      let list = JSON.parse(saved)
      // 移除当前视频的记录
      list = list.filter(item => +item.vid !== +vid)
      localStorage.setItem('playbackTimeList', JSON.stringify(list))
    } catch (e) {
      console.log('clear record error', e)
    }
  }

  initPlayback() {
    logger.debug('initPlayback')
    this.delegateOnPlayback(PlaybackEvent.PAUSED, () => {
      console.log('BROWSER_PAUSE11')
      this.ui.setPlay()
    })
    // 修正duration
    this.delegateOnPlayback(PlaybackEvent.CAN_PLAY, () => {
      if (this.playback.getDuration()) {
        this.ui.setDuration(this.playback.getDuration())
      }
    })
    this.delegateOnPlayback(PlaybackEvent.LOAD_START, () => {
      console.log('videoLoadStart')
      if (this.timing && !this.timing.videoLoadStart) {
        this.timing.videoLoadStart = Date.now()
      }
    })
    // 这里就算视频加载成功
    this.delegateOnPlayback(PlaybackEvent.LOAD_METADATA, () => {
      if (this.timing && !this.timing.videoLoadEnd) {
        this.timing.videoLoadEnd = Date.now()
        TIME.PLAYEND = new Date().getTime()
        const timeObj = {
          'init-playend': TIME.PLAYEND - TIME.INIT,
          'init-infostart': TIME.INFO_START - TIME.INIT,
          'infoend-infostart': TIME.INFO_END - TIME.INFO_START,
          'beforeplay-infoend': TIME.BEFORE_PLAY - TIME.INFO_END,
          'playstart-beforeplay': TIME.PLAYSTART - TIME.BEFORE_PLAY,
          'playend-playstart': TIME.PLAYEND - TIME.PLAYSTART,
        }
        console.log('playertimeing-', timeObj)
        this.timing = {
          ...this.timing,
          ...timeObj,
        }
      }
      this.ui.removeError()
    })

    this.delegateOnPlayback(PlaybackEvent.TIME_UPDATE, () => {
      const currentTime = this.playback.getCurrentTime()

      // 每播放5s记录一次播放进度
      if (currentTime - this.lastRecordTime >= 5) {
        this.recordPlayTime()
        this.lastRecordTime = currentTime
      }

      this.ui.setTime(currentTime)
    })
    this.delegateOnPlayback(PlaybackEvent.ENDED, () => {
      logger.debug('playback ENDED emitted')
      // 清空播放时间记录
      this.clearPlayTime()

      // 结束后暂停
      setTimeout(() => {
        this.playback.pause()
        this.ui.setTime(this.playback.getDuration())
      })

      this.ui.setBuffering(false)
      this.ui.setReplay()
      this.ui.emit(PlaybackEvent.ENDED)
      if (this.options.stop_cb && typeof window[this.options.stop_cb] === 'function') {
        window[this.options.stop_cb]()
      }
    })
    this.delegateOnPlayback(PlaybackEvent.BUFFERING, () => {
      logger.debug('playback BUFFERING emitted')
      this.ui.setBuffering(true)
    })
    this.delegateOnPlayback(PlaybackEvent.NOT_BUFFERING, () => {
      logger.debug('playback NOT_BUFFERING emitted')
      this.ui.setBuffering(false)
    })
    this.delegateOnPlayback(PlaybackEvent.PLAYING, () => {
      logger.debug('playback PLAYING emitted')
      this.ui.emit(PlaybackEvent.PLAYING)
    })
    this.delegateOnPlayback(PlaybackEvent.START_PROGRESS, () => {
      logger.debug('playback START_PROGRESS emitted')
    })
    this.delegateOnPlayback(PlaybackEvent.ERROR, () => {
      let err = this.playback.getError()
      logger.debug('playback ERROR emitted, err[', err, ']')
      this.ui.setError(err)
    })
    this.delegateOnPlayback(PlaybackEvent.BROWSER_PAUSE, () => {
      this.emit('BROWSER_PAUSE', false)
    })

    if (this.playback.isProgressed) {
      logger.debug('playback is already progressed')
      setTimeout(() => this.emit(PlaybackEvent.START_PROGRESS), 0)
    }
    if (this.playback.getError()) {
      let err = this.playback.getError()
      logger.debug('playback ERROR already emitted, err[', err, ']')
      this.ui.setError(err)
      setTimeout(() => this.emit(PlaybackEvent.ERROR), 0)
    }
  }
  initAd() {
    this.delegateOnAd(HuyaAdEvent.LOAD, () => {
      logger.debug('ad LOAD emitted')
    })
    this.delegateOnAd(HuyaAdEvent.CLICK, () => {
      logger.debug('ad CLICK emitted')
    })
    this.delegateOnAd(HuyaAdEvent.COMPLETE, () => {
      logger.debug('ad COMPLETE emitted')
    })
    this.delegateOnAd(HuyaAdEvent.ERROR, () => {
      logger.debug('ad ERROR emitted')
    })
    this.delegateOnAd(HuyaAdEvent.NO_AD, () => {
      logger.debug('ad NO_AD emitted')
    })
  }
  initStatistic() {
    logger.debug('initStatistic')
    this.statistic = new Statistic(this)
  }
  initDanmu() {
    logger.debug('initDanmu')
    const init = () => {
      this.danmu = new HuyaDanmu(this.$danmuContainer, this.$.extend({}, this.options), this)
    }
    this.afertLoadingVideo(() => {
      video_danmu_switch((err, data) => {
        if (err) {
          this.options.no_danmu = false
        } else if (Number(data) === -1) {
          this.options.no_danmu = true
        }
        if (this.options.no_danmu) {
          this.$container.find(this.selector.container).addClass(this.style.noDanmu)
          init()
        } else {
          // 查询视频是否为白名单视频与赛事视频
          videoLiveDanmuAllowList(this.options.vid, (err, res) => {
            if (!err) {
              const { isWhiteListUid, isMatchVideo } = res.data
              // const isMatchVideo = false
              // const isWhiteListUid = true
              // 是否在白名单列表中
              // if (isWhiteListUid) {
              //   this.ui.showDanmuType()
              //   // 是否为赛事
              //   if (isMatchVideo) {
              //     this.ui.showLiveDanmuPop()
              //   }
              //   this.options.isShowLiveDanmu = true
              // }
            }
            init()
          })
        }
      })
    })
  }
  // 播放视频
  playbackPlay() {
    TIME.PLAYSTART = new Date().getTime()
    this.$adContainer.hide()
    this.ui.enable()
    this.ui.setPause()
    this.ui.setBuffering(true)
    let p = this.playback.play()

    this.ui.setBuffering(false)

    logger.debug('showFrontAd complete')
  }
  // 广告 暂时不用
  showFrontAd() {
    logger.debug('showFrontAd')
    this.$coverContainer.remove()
    this.$adContainer.show()
    this.ad.showFrontAd(this.playbackPlay.bind(this))
  }
  delegateOnUI(type, cb) {
    this.delegateOn(this.ui, type, cb)
  }
  delegateOnPlayback(type, cb) {
    this.delegateOn(this.playback, type, cb)
  }
  delegateOnAd(type, cb) {
    this.delegateOn(this.ad, type, cb)
  }
  delegateOnDanmu(type, cb) {
    this.delegateOn(this.danmu, type, cb)
  }
  delegateOn(o, type, cb) {
    if (!o || !o.on) return
    o.on(type, (...args) => {
      cb.apply(this, args)
      this.emit(type, ...args)
    })
  }
  getVid() {
    if (!this.info) return ''
    else return this.options.vid
  }
  getChannelId() {
    if (!this.info) return ''
    else return this.info.getChannelId()
  }
  getLaiyuanv3() {
    return this.options.from
  }
  getVname() {
    if (!this.info) return ''
    else return this.info.getVname()
  }
  getVolume() {
    if (this.playback) {
      return this.playback.getVolume()
    } else {
      return 0
    }
  }
  getCurrentTime() {
    if (this.playback) {
      return this.playback.getCurrentTime()
    } else {
      return null
    }
  }
  getPlayerState() {
    if (this.playback) {
      return this.playback.getPlayerState()
    } else {
      return 'ready'
    }
  }
  getDuration() {
    if (this.playback) {
      return this.playback.getDuration()
    } else {
      return 60
    }
  }
  getVideoExtractData() {
    if (this.playback) {
      return this.playback.getVideoExtractData()
    } else {
      return 60
    }
  }

  // interface
  seek(value) {
    if (this.playback) {
      this.playback.seek(value)
      this.lastRecordTime = value // 重置记录时间
      this.recordPlayTime()
    }
  }

  // Huya Ad
  getAid() {
    if (this.ad) return this.ad.getAid()
    else return ''
  }
  getAti() {
    if (this.ad) return this.ad.getAti()
    else return ''
  }
  getCode() {
    if (this.ad) return this.ad.getCode()
    else return ''
  }

  // CDN
  getCDN() {
    if (this.info) return this.info.getCDN()
    else return ''
  }
  getCDNURL() {
    if (this.playback) return this.playback.getCurrentSrc()
    if (this.info) return this.info.getCDNURL()
    else return ''
  }
  getCDNError() {
    if (this.playback) return this.playback.getError().message
    else return null
  }

  // DANMU
  setDanmuValid(value) {
    this.ui && this.ui.setDanmuValid(value)
  }
  getDanmuContent() {
    if (this.ui) return this.ui.getDanmuContent()
    else return ''
  }
  danmuSubmitCountdown() {
    this.ui && this.ui.danmuSubmitCountdown()
  }

  afertLoadingVideo(callback) {
    const cb = () => {
      const definitionConfig = this.info.getDefinitionConfig()
      callback(this.timing, definitionConfig)
    }
    if (this.timing && this.timing.videoLoadEnd) {
      cb()
    } else {
      this.playback.once(PlaybackEvent.LOAD_METADATA, () => {
        cb()
      })
    }
  }
  initListenFeedback() {
    console.log('initListenFeedback')
    listenNotice(10220051, res => {
      console.log('视频手动反馈通知信令推送', res)
      const text = '后台手动触发反馈'
      this.sendFeedback(
        {
          errorText: text,
          content: text,
          detail: text,
          qq: '',
        },
        true,
      )
    })
  }
  // 初始化反馈
  initFeedBack() {
    // 创建FeedbackSdk实例
    this.feedback = new FeedbackSdk({
      // 应用版本
      appVersion: `Web视频站 ${packjson.version}`,
      // 视频id
      anchorId: this.options.vid,
      // 补贴
      subsid: 0,
      // 应用id
      appId: 2700,
      // 是否p2p
      isP2p: false,
    })
  }
  sendFeedback(data, isAuto) {
    let logModule = this.feedback.getFbModule('log')
    if (!logModule) {
      logModule = this.feedback.setFbModule('log')
    }
    logModule.sdk = this.playback.hyplayer.getLog()
    this.feedback
      .report(data)
      .then(res => {
        !isAuto &&
          Toast.show({
            content: '提交成功，感谢您的反馈',
            container: this.$playerContainer[0],
          })
      })
      .catch(error => {
        !isAuto &&
          Toast.show({
            content: '请勿频繁提交反馈',
            container: this.$playerContainer[0],
          })
      })
  }
}

export default HuyaPlayerCore
